import type { ExerciseItem } from './exercise';
import type { MainGoal, TrainingLevel, TrainingLocation } from './registration';
import type { ExerciseExecutionSpeed } from '../utils/executionSpeed';
import type { ExerciseRepetitionScope } from '../utils/repetitionScope';

export type TrainingCategory = 'postpartum' | 'pregnant' | 'performance' | 'fullbody' | 'rehabilitation' | 'perimenopause_menopause';
export type TrainingExerciseGroupingType = 'conjugate' | 'bi_set' | 'tri_set' | 'super_set' | 'circuit' | 'multiple_set';

export const TABATA_SUPPORTED_TRAINING_GROUPING_TYPES: TrainingExerciseGroupingType[] = [
  'conjugate',
  'circuit',
  'multiple_set',
];

export const isTabataSupportedTrainingGroupingType = (
  value?: TrainingExerciseGroupingType | null,
): value is TrainingExerciseGroupingType => (
  Boolean(value && TABATA_SUPPORTED_TRAINING_GROUPING_TYPES.includes(value))
);

export const TRAINING_EXERCISE_GROUPING_LABELS: Record<TrainingExerciseGroupingType, string> = {
  conjugate: 'Conjugado',
  bi_set: 'Bi-set',
  tri_set: 'Tri-set',
  super_set: 'Super-set',
  circuit: 'Circuito',
  multiple_set: 'Série múltipla',
};

export const TRAINING_EXERCISE_GROUPING_COLORS: Partial<Record<TrainingExerciseGroupingType, string>> = {
  conjugate: '#001e3a',
  bi_set: '#094174',
  tri_set: '#609bd1',
  super_set: '#82c7f5',
  circuit: '#bfe6ff',
  multiple_set: '#b2b2b2',
};

export const TRAINING_CATEGORY_OPTIONS = [
  { value: 'postpartum', label: 'Pós parto' },
  { value: 'pregnant', label: 'Gestante' },
  { value: 'performance', label: 'Performance' },
  { value: 'rehabilitation', label: 'Reabilitação' },
  { value: 'perimenopause_menopause', label: 'Perimenopausa/menopausa' },
  { value: 'fullbody', label: 'Fullbody' },
] as const;

export type StudentTrainingStatus =
  | 'assigned'
  | 'delivered'
  | 'active'
  | 'completed'
  | 'cancelled'
  | 'archived';

export const STUDENT_TRAINING_STATUS_LABELS: Record<StudentTrainingStatus, string> = {
  assigned: 'Atribuído',
  delivered: 'Entregue',
  active: 'Ativo',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  archived: 'Arquivado',
};

export function formatStudentTrainingStatus(status: string): string {
  return STUDENT_TRAINING_STATUS_LABELS[status as StudentTrainingStatus] ?? status;
}

export const TRAINING_CATEGORY_LABELS: Record<TrainingCategory, string> = {
  fullbody: 'Fullbody',
  postpartum: 'Pós parto',
  pregnant: 'Gestante',
  performance: 'Performance',
  rehabilitation: 'Reabilitação',
  perimenopause_menopause: 'Perimenopausa/menopausa',
};

export const WORKOUT_TRAINING_LEVEL_OPTIONS: Array<{ value: TrainingLevel; label: string }> = [
  { value: 'beginner', label: 'Iniciante' },
  { value: 'intermediate', label: 'Intermediário' },
  { value: 'advanced', label: 'Avançado' },
  { value: 'very_advanced', label: 'Muito Avançado' },
];

export interface TrainingItem {
  id: string;
  code?: string | null;
  name: string;
  category?: TrainingCategory;
  status?: string;
  splits: string[];
  loadIntensity: string;
  effortPerception: string;
  deliveredByName?: string;
  deliveredAt?: string;
  totalVolume: string;
  objective?: MainGoal[];
  descriptionLabel: string;
  description: string;
  group: string;
  month?: string;
  monthIndex?: number;
  location?: TrainingLocation | '';
  frequency?: string;
  sessionsPerWeek?: number;
  duration?: string;
  durationMinutes?: number;
  levels?: TrainingLevel[];
  level?: TrainingLevel | '';
  isActive?: boolean;
  primaryMuscleGroupId?: number | null;
  updatedAt?: string;
}

export interface TrainingBlockExercise {
  id: string;
  exerciseId: ExerciseItem['id'];
  name: string;
  group: string;
  objective: string;
  execution: TrainingExerciseExecution;
  muscleGroups: string[];
  groupingType?: TrainingExerciseGroupingType | null;
  conjugatedExercises?: TrainingBlockExercise[];
  hasExerciseDefaultsLoaded?: boolean;
}

export interface TrainingExerciseExecutionDetail {
  id: string;
  repetitionsMin: number | null;
  repetitionsMax: number | null;
  loadPercentage: number | null;
  rest: number | null;
  block: number | null;
}

export interface TrainingExerciseMethodConfig {
  roundRepetitions?: number | null;
  intraSetRest?: number | null;
  fixedRestMin?: number | null;
  fixedRestMax?: number | null;
}

export interface TrainingExerciseExecution {
  sets: number | null;
  repetitions: number | null;
  rest: number | null;
  method: string;
  details: TrainingExerciseExecutionDetail[];
  persistedSetIds?: number[];
  methodConfig?: TrainingExerciseMethodConfig | null;
  repetitionScope?: ExerciseRepetitionScope;
  executionSpeed?: ExerciseExecutionSpeed | null;
  isometricSeconds?: number | null;
  concentricSeconds?: number | null;
  pauseSeconds?: number | null;
  eccentricSeconds?: number | null;
  executionSeconds?: number | null;
  exerciseType?: 'strength' | 'cardio' | 'flexibility' | null;
}

export interface TrainingBlock {
  id: string;
  label: string;
  position?: number;
  items: TrainingBlockExercise[];
}

export interface TrainingFormValues {
  name: string;
  levels: TrainingLevel[];
  objective: MainGoal[];
  month: string;
  description: string;
  location: TrainingLocation | '';
  frequency: string;
  duration: string;
  category: TrainingCategory | '';
}

export type TrainingFormErrors = Partial<Record<keyof TrainingFormValues | 'blocks', string>>;

export interface TrainingSummaryMetric {
  label: string;
  value: string;
}

export interface TrainingMuscleVolume {
  label: string;
  value: string;
}

export interface TrainingSummaryBlock {
  blockId: string;
  totalVolume: string;
  totalTime: string;
  loadPercentage: string;
  loadIntensity: string;
  effortPerception: string;
}

export interface TrainingSummaryResponse {
  totalVolume: string;
  totalTime: string;
  loadPercentage: string;
  loadIntensity: string;
  effortPerception: string;
  blockSummaries: TrainingSummaryBlock[];
  muscleVolumes: TrainingMuscleVolume[];
}

export interface TrainingRecord extends TrainingItem {
  levels: TrainingLevel[];
  month: string;
  location: TrainingLocation | '';
  frequency: string;
  duration: string;
  blocks: TrainingBlock[];
  removedBlockIds?: number[];
  removedExerciseIds?: number[];
}